import { IWorkflow } from '@augloop/interfaces';
import { IResponse, ICacheDumpRequestResponse, Message, ResponseCallback, AnnotationResultsMessage, Response, SyncMessage, IErrorResponse, CacheDumpRequestMessage, ISessionCloseMessage, AnnotationResultState, AnnotationResultStateMessage } from '@augloop/session-protocol';
import { SchemaObject, AnnotationState, IOperation, ItemPath, AnnotationMetaData, UpdateAnnotationMetaDataOperation, ISchemaObject, SignalOperation } from '@augloop/types-core';
import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';

import { AnnotationActivationTracker } from './annotation-activation-tracker';
import { BatchManager, IBatch } from './batch-manager';
import { estimateSize } from './estimate-size';
import { IHostCallbacks, IBatchConfig, IBatchOptions, MultiplexResult, SplitResult } from './iclientruntime';
import { ISession, AnnotationActivationOptions, IAnnotationActivationResult, CacheRequestParameters, AnnotationCallback, SessionCloseCallback, SessionConnectCallback, SessionDisconnectCallback, SessionReconnectCallback } from './isession';
import { EmptySessionManager } from './websocket/empty-session-manager';
import { ISessionManager } from './websocket/isession-manager';
import { LocalWorkflowExecutionManager, IModelDownloader } from './local-workflow-execution-manager';

/**
 * Internal ISession interface that adds the initialization method.
 */
export interface ISessionInternal extends ISession {
    /**
     * Initialize the newly created session
     */
    initialize?(): Promise<ISession>;

    /**
     *
     * @returns Client metadata for the given session
     */
    getClientMetadata(): IClientMetadata;

    /**
     * Acknowledges receiving the annotation and notifies annotation activation callbacks
     *
     * @param message Contains the annotation type and operations to be applied
     *
     * @param responseCallback Callback run after receiving the annotations
     */
    onAnnotationResults(message: AnnotationResultsMessage, responseCallback: ResponseCallback): void;
}

export type SessionConfig = {
    hostCallbacks: IHostCallbacks;
    sessionManager: ISessionManager;
    batchOptions: IBatchOptions;
    extensionConfigs: ISchemaObject[];
    clientMetadata: IClientMetadata;
    modelDownloader?: IModelDownloader;
}

export type SessionFactory = (config: SessionConfig) => ISessionInternal;

export class Session implements ISessionInternal {
    private readonly hostCallbacks: IHostCallbacks;
    private sessionManager: ISessionManager;
    private readonly annotationActivationTrackers: Map<string, AnnotationActivationTracker> = new Map();
    private readonly annotationCallbacks: Map<string, Map<string, AnnotationCallback>> = new Map();
    private readonly annotationResultStates: Map<string, AnnotationResultState> = new Map();
    private readonly tokensByAnnotationType: Map<string, string[]> = new Map();
    private nextSyncSequenceId: number = 1;
    private allowSeed: boolean = true;
    private allowGroupSeed: boolean = true;
    private seedGroupSize: number = 0;
    private hasSessionConnected: boolean = false;
    private isSessionClosed: boolean = false;
    private isSessionConnected: boolean = false;
    private batchedOperationsManager: BatchManager;
    private seedBatchedOperationsManager: BatchManager;
    private operationBatchConfig: IBatchConfig<IOperation[]>;
    private extensionConfigs: ISchemaObject[];
    private sessionCloseCallback: SessionCloseCallback;
    private connectCallback: SessionConnectCallback;
    private reconnectCallback: SessionReconnectCallback;
    private clientMetadata: IClientMetadata;
    private localWorkflowManager: LocalWorkflowExecutionManager;
    private disconnectCallback: SessionDisconnectCallback;
    private modelDownloader: IModelDownloader;

    constructor(config: SessionConfig) {
        this.hostCallbacks = config.hostCallbacks;
        this.sessionManager = config.sessionManager;
        this.operationBatchConfig = config.batchOptions ? this.getOperationBatchConfig(config.batchOptions) : undefined;
        this.extensionConfigs = config.extensionConfigs;
        this.clientMetadata = config.clientMetadata;
        this.modelDownloader = config.modelDownloader;

        this.sessionManager.on('sessionClose', this.onSessionClose.bind(this));
        this.sessionManager.on('reconnect', this.onReconnect.bind(this));
        this.sessionManager.on('connect', this.onConnect.bind(this));
        this.sessionManager.on('disconnect', this.onDisconnect.bind(this));
        this.sessionManager.on('resetNextSyncSequenceId', this.resetNextSyncSequenceId.bind(this));
        this.sessionManager.onMessage(AnnotationResultsMessage.getTypeName(), this.onAnnotationResults.bind(this));
        this.sessionManager.onMessage(AnnotationResultStateMessage.getTypeName(), this.onAnnotationResultStateMessage.bind(this));
    }

    public initialize(): Promise<ISession> {
        return this.sessionManager.init(this.extensionConfigs, this.hostCallbacks.onInitSession ? this.hostCallbacks.onInitSession : undefined)
            .then(() => { return this; });
    }

    public get isConnected(): boolean {
        return this.isSessionConnected;
    }

    public get hasConnected(): boolean {
        return this.hasSessionConnected;
    }

    public get isClosed(): boolean {
        return this.isSessionClosed;
    }

    public registerLocalWorkflow(workflow: IWorkflow): void {
        if (!this.localWorkflowManager) {
            this.localWorkflowManager = new LocalWorkflowExecutionManager(this, this.modelDownloader);
        }
        this.localWorkflowManager.registerLocalWorkflow(workflow);
    }

    public activateAnnotation(annotationType: string, options?: AnnotationActivationOptions): Promise<IAnnotationActivationResult> {
        const tracker = new AnnotationActivationTracker(this.getSessionManager.bind(this), annotationType, options);

        if (options && options.callback) {
            let map: Map<string, AnnotationCallback> = this.annotationCallbacks.get(annotationType);
            if (!map) {
                map = new Map<string, AnnotationCallback>();
                this.annotationCallbacks.set(annotationType, map);
            }
            map.set(tracker.token, options.callback);
        }

        const tokens = this.tokensByAnnotationType.get(annotationType) ?? [];
        if (tokens.indexOf(tracker.token) === -1) {
            tokens.push(tracker.token);
        }
        this.tokensByAnnotationType.set(annotationType, tokens);

        this.annotationActivationTrackers.set(tracker.token, tracker);
        tracker.activate(false/*ignoreExistingAnnotations*/);
        return tracker.asPromise();
    }

    public updateAnnotationConfig(_token: string, _config: SchemaObject): void {
        throw new Error('Not Yet Implemented');
    }

    public releaseAnnotation(token: string): Promise<boolean> {
        const tracker = this.annotationActivationTrackers.get(token);

        if (tracker) {
            this.annotationActivationTrackers.delete(token);

            const map: Map<string, AnnotationCallback> = this.annotationCallbacks.get(tracker.annotationType);
            if (map) {
                map.delete(token);
                if (map.size == 0) {
                    this.annotationCallbacks.delete(tracker.annotationType);
                }
            }

            return tracker.release();
        }

        return Promise.resolve(false);
    }

    public setAnnotationState(parentPath: ItemPath, id: string, value: AnnotationState): void {
        const M_: AnnotationMetaData = { state: value };
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath, items: [{ id: id }], M_ }));
    }

    public setAnnotationMetadata(parentPath: ItemPath, id: string, value: AnnotationMetaData): void {
        this.submitOperation(new UpdateAnnotationMetaDataOperation({ parentPath, items: [{ id: id }], M_: value }));
    }

    public submitOperation(operation: IOperation, cv?: string): void {
        this.submitOperations([operation], cv);
    }

    public submitOperations(operations: IOperation[], cv?: string): void {
        const isSignalOp = (op: IOperation) => SchemaObject.getTypeNameFor(op) === SignalOperation.getTypeName();
        const signalOps = operations.filter(op => isSignalOp(op));
        const modelOps = operations.filter(op => !isSignalOp(op));

        // Signal ops are sent immediately regardless of batch config and are not sequenced
        if (signalOps.length > 0) {
            this.getSessionManager().sendMessage(
                new SyncMessage({
                    cv: cv,
                    ops: signalOps
                }));
        }

        if (modelOps.length > 0) {
            if (this.operationBatchConfig) {
                if (!this.batchedOperationsManager) {
                    const submit = (
                        operations: IOperation[],
                        batch: IBatch<IOperation>,
                        callback: (err?: any, output?: any) => void
                    ): void => {
                        this.getSessionManager().sendMessage(
                            new SyncMessage({
                                cv: batch.cv,
                                seq: this.nextSyncSequenceId++,
                                ops: operations
                            }),
                            (errorResponse: IErrorResponse): void => {
                                callback(errorResponse ? new Error(errorResponse.error) : undefined);
                            }
                        );
                    };
                    this.batchedOperationsManager = new BatchManager(submit);
                }

                this.batchedOperationsManager.addBatchItem(modelOps, this.operationBatchConfig, { name: 'SubmitOperations' }, cv);
            } else {
                this.getSessionManager().sendMessage(
                    new SyncMessage({
                        cv: cv,
                        seq: this.nextSyncSequenceId++,
                        ops: modelOps
                    }));
            }
        }

        if (this.localWorkflowManager) {
            this.localWorkflowManager.runLocalWorkflows(operations);
        }
    }

    public submitSeedOperations(operations: IOperation[], cv?: string): void {
        if (!this.allowSeed) {
            throw new Error('Cannot submit seed operations more than once per session');
        }

        this.allowSeed = false;
        this.allowGroupSeed = false;

        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, true, cv);
        } else {
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                    cv: cv,
                    seq: 0,
                    ops: operations
                }));
        }
    }

    public submitSeedGroupOperations(operations: IOperation[], groupComplete?: boolean, cv?: string): void {
        if (!this.allowGroupSeed) {
            throw new Error('Seed operations are not allowed for this session');
        }
        if (groupComplete) {
            this.allowGroupSeed = false;
        }
        this.allowSeed = false;

        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, !!groupComplete, cv);
        } else {
            this.seedGroupSize++;
            this.getSessionManager()
                .sendMessage(new SyncMessage({
                    cv: cv,
                    seq: 0,
                    ops: operations,
                    groupId: 'Seed',
                    groupSize: groupComplete ? this.seedGroupSize : undefined,
                    groupComplete: groupComplete ? groupComplete : undefined
                }));
        }
    }

    public submitCustomMessage(message: Message): Promise<IResponse> {
        return new Promise((resolve, reject) => {
            this.getSessionManager()
                .sendMessage(message, (err: IErrorResponse, response: IResponse) => {
                    if (err) {
                        reject(err);
                    } else {
                        resolve(response);
                    }
                });
        });
    }

    public requestCacheDump(cacheRequestParameters?: CacheRequestParameters): Promise<ICacheDumpRequestResponse> {
        if (cacheRequestParameters) {
            throw new Error('NYI');
        } else {
            const toReturn = new Promise<ICacheDumpRequestResponse>((resolve, reject) => {
                this.getSessionManager()
                    .sendMessage(new CacheDumpRequestMessage(), ((err?: IErrorResponse, response?: ICacheDumpRequestResponse) => {
                        if (!err) {
                            resolve(response);
                        } else {
                            reject(err);
                        }
                    }));
            });
            return toReturn;
        }
    }

    public forceReconnect(extensionConfigs?: ISchemaObject[]): Promise<void> {
        this.extensionConfigs = extensionConfigs;
        return this.getSessionManager().forceReconnect(extensionConfigs);
    }

    public close(): void {
        this.isSessionClosed = true;
        this.getSessionManager().closeSession();
    }

    public getClientMetadata(): IClientMetadata {
        return this.clientMetadata;
    }

    public setSessionCloseCallback(callback: SessionCloseCallback): void {
        this.sessionCloseCallback = callback;
    }

    public setConnectCallback(callback: SessionConnectCallback): void {
        this.connectCallback = callback;
    }

    public setDisconnectCallback(callback: SessionDisconnectCallback): void {
        this.disconnectCallback = callback;
    }

    public setReconnectCallback(callback: SessionReconnectCallback): void {
        this.reconnectCallback = callback;
    }

    public onAnnotationResults(message: AnnotationResultsMessage, responseCallback: ResponseCallback): void {
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());

        // Notify annotation activation callbacks
        for (const op of message.ops) {
            this.callAnnotationCallbacks(op, message.annotationType);
            if (this.hostCallbacks.onAnnotationResult) {
                this.hostCallbacks.onAnnotationResult(op);
            }
        }
    }

    public onAnnotationResultStateMessage(message: AnnotationResultStateMessage, responseCallback: ResponseCallback): void {
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new Response());

        for (const update of message.updates) {
            const { annotationType, state } = update;

            if (!this.tokensByAnnotationType.has(annotationType)) {
                continue;
            }

            // Get or determine old state if it's not available.
            // Updates are only sent if state has changed, so we can
            // determind the old state if it's not previously known.
            let oldState: AnnotationResultState;
            if (this.annotationResultStates.has(annotationType)) {
                oldState = this.annotationResultStates.get(annotationType);
            } else {
                if (state === AnnotationResultState.Idle) {
                    oldState = AnnotationResultState.Pending;
                } else {
                    oldState = AnnotationResultState.Idle;
                }
            }

            for (const token of this.tokensByAnnotationType.get(annotationType)) {
                const tracker = this.annotationActivationTrackers.get(token);
                if (tracker.options?.stateUpdateCallback) {
                    tracker.options.stateUpdateCallback(oldState, state);
                }
            }
        }
    }

    private resetNextSyncSequenceId(): void {
        this.nextSyncSequenceId = 1;
    }

    private onReconnect(): void {
        this.isSessionConnected = true;
        this.annotationActivationTrackers.forEach((tracker) => {
            tracker.activate(true/*ignoreExistingAnnotations*/);
        });

        if (this.reconnectCallback) {
            this.reconnectCallback();
        }
    }

    private onSessionClose(sessionCloseMessage: ISessionCloseMessage): void {
        this.isSessionClosed = true;
        this.isSessionConnected = false;
        if (this.sessionCloseCallback) {
            this.sessionCloseCallback(sessionCloseMessage);
        }
    }

    private onConnect(isSeedingRequired: boolean, sessionUrl: string, origin: string, authToken: string): void {
        if (this.hasSessionConnected) {
            // If seeding is required on reconnect reset allowSeed flags, seedGroupSize counter, and seedBatchedOperationsManager.
            // Otherwise we will continue with already started seeding if reconnect occurred during the seeding
            if (isSeedingRequired) {
                this.allowSeed = true;
                this.allowGroupSeed = true;
                this.seedGroupSize = 0;
                if (this.seedBatchedOperationsManager) {
                    this.seedBatchedOperationsManager.removeAllBatchedItems();
                    this.seedBatchedOperationsManager = undefined;
                }
                if (this.batchedOperationsManager) {
                    this.batchedOperationsManager.removeAllBatchedItems();
                    this.batchedOperationsManager = undefined;
                }
            }
        }

        this.isSessionConnected = true;
        this.hasSessionConnected = true;
        if (this.connectCallback) {
            this.connectCallback(isSeedingRequired, sessionUrl, origin, authToken);
        }
    }

    private onDisconnect(error: string): void {
        this.isSessionConnected = false;
        if (this.disconnectCallback) {
            this.disconnectCallback(error);
        }
    }

    private getSessionManager(): ISessionManager {
        if (!this.sessionManager) {
            this.sessionManager = new EmptySessionManager();
        }
        return this.sessionManager;
    }

    private callAnnotationCallbacks(op: IOperation, annotationType: string): void {
        if (this.annotationCallbacks.size === 0) {
            return;
        }

        const callbackMap: Map<string, AnnotationCallback> = this.annotationCallbacks.get(annotationType);
        if (callbackMap) {
            callbackMap.forEach((callback) => {
                callback(op);
            });
        }
    }

    private getOperationBatchConfig(batchOptions: IBatchOptions): IBatchConfig<IOperation[]> {
        // Group all arrays of operations together
        const groupingKeyExtractor = (): string => {
            return 'operations';
        };

        // Combine operations into a single array
        const multiplex = (operations: IOperation[][]): MultiplexResult => {
            return {
                input: operations.filter(ops => ops.length).reduce((ops, array) => ops.concat(array), []),
                demultiplex: () => [[]] // NOTE: Unused because no callback is specified to addBatchItem
            };
        };

        // Split large operations arrays into smaller arrays
        const split = (operations: IOperation[]): SplitResult | undefined => {
            // Cannot split array with 1 or fewer operations
            if (operations.length <= 1) {
                return undefined;
            }

            return {
                inputs: operations.reduce((ops, op) => {
                    ops.push([op]);
                    return ops;
                }, []),
                join: () => [] // NOTE: Unused because no callback is specified to addBatchItem
            };
        };

        return {
            delayMs: batchOptions.delayMs,
            maxInputSize: batchOptions.maxInputSize,
            estimateSize: estimateSize,
            groupingKeyExtractor: groupingKeyExtractor,
            multiplex: multiplex,
            split: split
        };
    }

    private sendSeedMessagesViaBatchManager(operations: IOperation[], groupComplete: boolean, cv?: string): void {
        if (!this.seedBatchedOperationsManager) {
            // Submit will be invoked once per batch of operations
            const submit = (
                operations: IOperation[],
                batch: IBatch<IOperation>,
                callback: (err?: any, output?: any) => void
            ): void => {
                // Update the count of batches
                this.seedGroupSize++;

                // Send the SyncMessage for the operations in this batch
                this.getSessionManager().sendMessage(
                    new SyncMessage({
                        cv: batch.cv,
                        seq: 0,
                        ops: operations,
                        groupId: 'Seed',
                        groupSize: batch.groupComplete ? this.seedGroupSize : undefined,
                        groupComplete: batch.groupComplete ? batch.groupComplete : undefined
                    }),
                    (errorResponse: IErrorResponse): void => {
                        callback(errorResponse ? new Error(errorResponse.error) : undefined);
                    }
                );
            };
            // Instantiate the seed batch manager
            this.seedBatchedOperationsManager = new BatchManager(submit);
        }

        // Instantiate the seed batch manager and add the seed operations to it
        this.seedBatchedOperationsManager.addBatchItem(operations, this.operationBatchConfig, { name: 'SubmitSeedOperations' }, cv, groupComplete);
    }
}
